/* eslint-disable no-unused-vars */
/* eslint-disable indent */
import React, { useState, useEffect } from "react";
import { Card, Row, Col, message, Button } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import moment from "moment";
import {
    DropDownComponent,
    TableComponent,
    CalendarComponent,
    RefreshTimestamp
} from "../../CommonComponents";
import "../styles.scss";
import Heading from "../../../../components/Heading";
import { getHubList } from "../../../../services/api/hub";
import { ReportsService } from "../../../../services/api/reports";
import { downloadExcessInscanEnhancedReport } from "../../../../utils/downloadUtils";
import { useRequestCancellation } from "../../../../hooks/useRequestCancellation";
import constants from "../../../../constants/constants";

const { ROLES } = constants;
const { HUB_ADMIN, HO_ADMIN } = ROLES;

const ExcessInScanEnhancedReport = () => {
    // Global request cancellation hook
    const { createNewRequest, isLatestRequest } = useRequestCancellation();

    const [loading, setLoading] = useState(false);
    const [downloadLoading, setDownloadLoading] = useState(false);
    const [dateRange, setDateRange] = useState([
        moment().subtract(2, "days"),
        moment()
    ]);
    const [hubList, setHubList] = useState([]);
    const [selectedExcessInscanHubs, setSelectedExcessInscanHubs] = useState([]);
    const [selectedBookingUpdatedHubs, setSelectedBookingUpdatedHubs] = useState([]);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 50,
        total: 0,
        showSizeChanger: true,
        pageSizeOptions: ["50", "100", "200", "500"],
        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`
    });
    const [reportData, setReportData] = useState([]);
    const [hubUser, setHubUser] = useState();
    const [lastsynced, setLastsynced] = useState(null);
    const [nextScheduledRefresh, setNextScheduledRefresh] = useState(null);

    // Table columns definition
    const columns = [
        {
            title: "AWB Number",
            dataIndex: "awbNumber",
            key: "awbNumber",
            width: 180,
            fixed: "left",
            //AWB Number should be a link
            render: (text, record) => (
                <span
                    style={{ color: "#1890ff", cursor: "pointer", textDecoration: "underline" }}
                    onClick={() => {
                        const url = `/shipment-tracker?awb=${text}`;
                        window.open(url, "_blank");
                    }}
                >
                    {text}
                </span>
            )
        },
        {
            title: "Excess Inscan Hub Name",
            dataIndex: "excessInscanHubName",
            key: "excessInscanHubName",
            width: 200
        },
        {
            title: "Next Scan Premise Names",
            dataIndex: "nextScanPremiseNames",
            key: "nextScanPremiseNames",
            width: 200,
            render: (text) => text || "-"
        },
        {
            title: "Excess Inscan Time",
            dataIndex: "excessInscanTime",
            key: "excessInscanTime",
            width: 180,
            render: (text) => text ? moment(text).format("YYYY-MM-DD HH:mm:ss") : "-"
        },
        {
            title: "Booking Updated At",
            dataIndex: "bookingUpdatedAt",
            key: "bookingUpdatedAt",
            width: 180,
            render: (text) => text ? moment(text).format("YYYY-MM-DD HH:mm:ss") : "-"
        },
        {
            title: "Booking Updated Time",
            dataIndex: "bookingUpdatedTime",
            key: "bookingUpdatedTime",
            width: 180,
            render: (text) => text ? moment(text).format("YYYY-MM-DD HH:mm:ss") : "-"
        }
    ];

    // Combined initial setup useEffect
    useEffect(() => {
        (async () => {
            try {
                setLoading(true);
                const hubs = await getHubList();
                setHubList(hubs);
            } catch (error) {
                console.error("Error loading hub list:", error);
                message.error("Failed to fetch hub list");
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    // Set default hub for HUB_ADMIN users
    useEffect(() => {
        const user = JSON.parse(localStorage.getItem("userinfo") || "{}");
        const userId = JSON.parse(localStorage.getItem("userId") || "{}");

        console.log("User info:", user);
        console.log("User ID:", userId);

        setHubUser(user?.roles?.[0]);

        const activeHub = JSON.parse(localStorage.getItem("active_hub"));
        const activeHubId = activeHub?.hubId;

        if (hubList.length > 0) {
            if (activeHubId) {
                const defaultHub = hubList.find((hub) => String(hub.value) === String(activeHubId));
                if (defaultHub) {
                    setSelectedExcessInscanHubs([defaultHub.value]);
                }
            } else if ((user?.roles?.[0] === "ROLE_HUBADMIN" || user?.roles?.[0] === "ROLE_HUBOPERATOR")) {
                const defaultHub = hubList.find(hub => String(hub.value) === String(userId?.userInfo?.premiseId));
                if (defaultHub) {
                    setSelectedExcessInscanHubs([defaultHub.value]);
                }
            }
        }
    }, [hubList]);

    // Debounced effect for report fetching - prevents rapid API calls during filter changes
    useEffect(() => {
        // Wait for hubUser to be initialized
        if (!hubUser) {
            return;
        }

        // Debounce the API call to prevent multiple rapid calls when resetting filters
        const timer = setTimeout(() => {
            if (dateRange && dateRange[0] && dateRange[1]) {
                fetchReport();
            } else {
                setReportData([]);
                setPagination(prev => ({
                    ...prev,
                    total: 0
                }));
            }
        }, 300); // 300ms debounce

        return () => clearTimeout(timer);
        // eslint-disable-next-line
    }, [hubUser, selectedExcessInscanHubs, selectedBookingUpdatedHubs, dateRange, pagination.current, pagination.pageSize]);

    const fetchReport = async () => {
        // 🔥 CREATE NEW REQUEST WITH GLOBAL HOOK
        const { signal, requestId } = createNewRequest("excess-inscan-enhanced-report");

        try {
            setLoading(true);
            setReportData([]); // Clear previous data

            // Validate dates
            if (!dateRange || !Array.isArray(dateRange) || !dateRange[0] || !dateRange[1]) {
                setLoading(false);
                message.warning("Please select a valid date range");
                return;
            }

            // Ensure we have moment objects
            const startDate = moment(dateRange[0]);
            const endDate = moment(dateRange[1]);

            if (!startDate.isValid() || !endDate.isValid()) {
                setLoading(false);
                message.warning("Invalid date range selected");
                return;
            }

            const params = {
                fromDate: startDate.format("YYYY-MM-DD"),
                toDate: endDate.format("YYYY-MM-DD"),
                excessInscanHubIds: selectedExcessInscanHubs.length > 0 ? selectedExcessInscanHubs.join(",") : "",
                bookingUpdatedHubIds: selectedBookingUpdatedHubs.length > 0 ? selectedBookingUpdatedHubs.join(",") : "",
                page: (pagination.current - 1), // Convert to 0-based
                size: pagination.pageSize
            };

            console.log("Excess Inscan Enhanced Report params:", params);

            const response = await ReportsService.getExcessInscanEnhancedReport(params, { signal });

            // 🔥 ONLY UPDATE IF THIS IS THE LATEST REQUEST
            if (!isLatestRequest(requestId)) {
                console.log("🔥 Request cancelled - ignoring response");
                return;
            }

            setLastsynced(response.lastRefreshedAt);
            setNextScheduledRefresh(response.nextScheduledRefresh);
            setReportData(response.data || []);
            setPagination(prev => ({
                ...prev,
                total: response.total || 0,
            }));

        } catch (error) {
            // 🔥 DON"T UPDATE STATE IF REQUEST WAS CANCELLED
            if (error.name === "AbortError") {
                console.log("🔥 Request was cancelled at HTTP level - no state update");
                return;
            }

            // Only update if this is still the latest request
            if (!isLatestRequest(requestId)) {
                console.log("🔥 Request cancelled - ignoring error");
                return;
            }

            console.error("Failed to fetch Excess Inscan Enhanced report:", error);
            message.error("Failed to fetch report data");
        } finally {
            setLoading(false);
        }
    };

    const handleTableChange = (newPagination) => {
        setPagination(prev => ({
            ...prev,
            current: newPagination.current,
            pageSize: newPagination.pageSize
        }));
    };


    const handleDownload = async () => {
        if (!dateRange || !Array.isArray(dateRange) || !dateRange[0] || !dateRange[1]) {
            message.warning("Please select a valid date range");
            return;
        }

        try {
            setDownloadLoading(true);

            // Ensure we have moment objects
            const startDate = moment(dateRange[0]);
            const endDate = moment(dateRange[1]);

            if (!startDate.isValid() || !endDate.isValid()) {
                message.warning("Invalid date range selected");
                setDownloadLoading(false);
                return;
            }

            const params = {
                fromDate: startDate.format("YYYY-MM-DD"),
                toDate: endDate.format("YYYY-MM-DD"),
                excessInscanHubIds: selectedExcessInscanHubs.length > 0 ? selectedExcessInscanHubs.join(",") : "",
                bookingUpdatedHubIds: selectedBookingUpdatedHubs.length > 0 ? selectedBookingUpdatedHubs.join(",") : ""
            };

            console.log("Download params:", params);

            await downloadExcessInscanEnhancedReport(params);
            message.success("Report download initiated");
        } catch (error) {
            console.error("Error downloading report:", error);
            message.error("Failed to download report");
        } finally {
            setDownloadLoading(false);
        }
    };

    return (
        <div className="report-container">
            <div style={{ backgroundColor: "white", padding: "8px 16px", borderRadius: "8px" }}>
                <Heading title="Excess Inscan Enhanced Report">
                    <div style={{ paddingTop: "5px" }}>
                        <RefreshTimestamp
                            lastRefreshedAt={lastsynced}
                            nextScheduledRefresh={nextScheduledRefresh}
                        />
                    </div>
                </Heading>
            </div>
            <Card className="report-card">
                <Row gutter={[16, 16]} className="filters-section">
                    <Col xs={24} sm={12} md={8} lg={6}>
                        <div className="filter-label">Date Range</div>
                        <CalendarComponent
                            className="custom-calendar"
                            type="range"
                            value={dateRange}
                            onChange={(value) => {
                                setDateRange(value);
                                setPagination(prev => ({ ...prev, current: 1 }));
                            }}
                            format="YYYY-MM-DD"
                            allowClear
                        />
                    </Col>

                    <Col xs={24} sm={12} md={8} lg={6}>
                        <div className="filter-label">Excess Inscan Hub(s)</div>
                        <DropDownComponent
                            className="small-dropdown"
                            placeholder="Select Excess Inscan Hub(s)"
                            options={hubList}
                            onChange={(value) => {
                                setSelectedExcessInscanHubs(value);
                                setPagination(prev => ({ ...prev, current: 1 }));
                            }}
                            value={selectedExcessInscanHubs}
                            mode="multiple"
                            allowClear
                            maxTagCount="responsive"
                            showSearch={true}
                            optionFilterProp="label"
                            filterOption={(input, option) =>
                                (option?.label || "").toLowerCase().includes(input.toLowerCase())
                            }
                        />
                    </Col>

                    <Col xs={24} sm={12} md={8} lg={6}>
                        <div className="filter-label">Booking Updated Hub(s)</div>
                        <DropDownComponent
                            className="small-dropdown"
                            placeholder="Select Booking Updated Hub(s)"
                            options={hubList}
                            onChange={(value) => {
                                setSelectedBookingUpdatedHubs(value);
                                setPagination(prev => ({ ...prev, current: 1 }));
                            }}
                            value={selectedBookingUpdatedHubs}
                            mode="multiple"
                            allowClear
                            maxTagCount="responsive"
                            showSearch={true}
                            optionFilterProp="label"
                            filterOption={(input, option) =>
                                (option?.label || "").toLowerCase().includes(input.toLowerCase())
                            }
                        />
                    </Col>

                    <Col xs={24} sm={12} md={8} lg={6}>
                        <div className="filter-label">&nbsp;</div>
                        <Button
                            type="primary"
                            icon={<DownloadOutlined />}
                            onClick={handleDownload}
                            loading={downloadLoading}
                            block
                        >
                            Download Report
                        </Button>
                    </Col>
                </Row>

                <Row style={{ marginTop: "24px" }}>
                    <Col span={24}>
                        <TableComponent
                            columns={columns}
                            dataSource={reportData}
                            loading={loading}
                            pagination={pagination}
                            onChange={handleTableChange}
                            rowKey="awbNumber"
                            scroll={{ x: 1500 }}
                        />
                    </Col>
                </Row>
            </Card>
        </div>
    );
};

export default ExcessInScanEnhancedReport;

